<template>
    <div>
        <el-form @submit.native.prevent="submitRequest" class="proposal-request" v-loading="loading" :model="formData"
            ref="formProposalNew">
            <el-alert v-if="fetchError" :title="fetchError" type="warning" show-icon :closable="false" class="mb-4" />
            <div v-if="lead.first_name">
                <h6 class="heading-small text-muted mb-4">
                    New Proposal Request
                </h6>
                <div class="row">
                    <div class="col-lg-10">
                        <h3>
                            <span class="text-muted">For</span> {{ lead.first_name }}
                            {{ lead.last_name }}
                        </h3>
                        <small class="text-muted">Event Date: {{ eventStartDisplay }}</small>
                        <!-- <small class="text-muted">Event Date: {{ eventDateDisplay }}</small> -->
                    </div>
                </div>
            </div>

            <hr />

            <h6 class="heading-small text-muted mb-4">
                <i class="ni ni-building"></i> Select Venues
            </h6>

            <template v-if="shortlists?.length">
                <div v-for="venue in shortlists" :key="`shortlist-for-proposal-${venueKey(venue)}`" class="venue-row mb-3 pb-3 border-bottom">
                    <div class="row align-items-start">
                        <div class="col-lg-4">
                            <base-checkbox v-model="formData.venues[venueKey(venue)]" @input="onVenueToggle(venue)">
                                <div>
                                    <strong>{{ venue.name }}</strong> <span v-if="venue.is_custom_venue">(Custom Venue)</span>
                                </div>
                            </base-checkbox>
                        </div>
                        <div class="col-lg-8" v-if="formData.venues[venueKey(venue)]">
                            <div class="d-flex align-items-center">
                                <span class="text-muted small mr-2 flex-shrink-0" style="white-space:nowrap;">Contact
                                    name:</span>
                                <span v-if="contactNameOverrides[venueKey(venue)] === undefined && getDefaultContactName(venue)"
                                    class="small mr-2">
                                    {{ getDefaultContactName(venue) }}
                                </span>
                                <input v-else v-model="contactNameOverrides[venueKey(venue)]"
                                    placeholder="Contact name (required)..." class="form-control form-control-sm mr-2"
                                    :class="{ 'is-invalid': contactNameInvalid[venueKey(venue)] }" style="max-width: 380px;"
                                    type="text" @input="validateContactName(venueKey(venue))" />
                                <a v-if="getDefaultContactName(venue)" href="javascript:"
                                    class="small text-primary flex-shrink-0" @click="toggleContactNameOverride(venue)">
                                    {{ contactNameOverrides[venueKey(venue)] !== undefined ? 'Reset to default' : 'Override name'
                                    }}
                                </a>
                            </div>
                            <div v-if="contactNameOverrides[venueKey(venue)] !== undefined" class="mt-1 ml-0"
                                style="padding-left: calc(7.5rem + 1px);">
                                <small v-if="contactNameInvalid[venueKey(venue)]" class="text-danger">
                                    <i class="ni ni-notification-70"></i> Contact name is required.
                                </small>
                                <small v-else-if="getDefaultContactName(venue)" class="text-warning">
                                    <i class="ni ni-notification-70"></i> Override applies to this send only — the saved
                                    venue contact will not be changed.
                                </small>
                            </div>

                            <div class="d-flex align-items-center mt-2">
                                <span class="text-muted small mr-2 flex-shrink-0" style="white-space:nowrap;">Proposal
                                    email:</span>
                                <span v-if="emailOverrides[venueKey(venue)] === undefined" class="small mr-2">
                                    {{ (venueInfo[venueKey(venue)] && venueInfo[venueKey(venue)].inspection_contact_email) ||
                                        venue.inspection_contact_email || '(no email on file)' }}
                                </span>
                                <input v-else v-model="emailOverrides[venueKey(venue)]" placeholder="Override email address..."
                                    class="form-control form-control-sm mr-2" style="max-width: 380px;" type="email" />
                                <a href="javascript:" class="small text-primary flex-shrink-0"
                                    @click="toggleEmailOverride(venue)">
                                    {{ emailOverrides[venueKey(venue)] !== undefined ? 'Reset to default' : 'Override email' }}
                                </a>
                            </div>
                            <div v-if="emailOverrides[venueKey(venue)] !== undefined" class="mt-1 ml-0"
                                style="padding-left: calc(7.5rem + 1px);">
                                <small class="text-warning">
                                    <i class="ni ni-notification-70"></i> Override applies to this send only — the saved
                                    venue email will not be changed.
                                </small>
                            </div>

                            <div class="d-flex align-items-center mt-2">
                                <span class="text-muted small mr-2 flex-shrink-0" style="white-space:nowrap;">CC:</span>
                                <span v-if="ccEmails[venueKey(venue)] === undefined" class="small text-muted mr-2">
                                    (no CC)
                                </span>
                                <input v-else v-model="ccEmails[venueKey(venue)]" placeholder="cc1@example.com, cc2@example.com"
                                    class="form-control form-control-sm mr-2" :class="{ 'is-invalid': ccInvalid[venueKey(venue)] }"
                                    style="max-width: 380px;" type="text" @input="validateCc(venueKey(venue))" />
                                <a href="javascript:" class="small text-primary flex-shrink-0" @click="toggleCc(venue)">
                                    {{ ccEmails[venueKey(venue)] !== undefined ? 'Remove CC' : 'Add CC' }}
                                </a>
                            </div>
                            <div v-if="ccEmails[venueKey(venue)] !== undefined" class="mt-1 ml-0"
                                style="padding-left: calc(7.5rem + 1px);">
                                <small v-if="ccInvalid[venueKey(venue)]" class="text-danger">
                                    <i class="ni ni-notification-70"></i> One or more CC email addresses are invalid.
                                </small>
                                <small v-else class="text-muted">
                                    Separate multiple addresses with commas.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <div v-if="!shortlists || !shortlists.length" class="text-muted text-sm mb-3">
                No venues on the shortlist yet.
            </div>

            <hr />

            <h6 class="heading-small text-muted mb-4">
                <i class="ni ni-email-83"></i> Email Content
            </h6>

            <div class="card bg-light p-4 mb-4 email-template-preview">
                <p class="text-xs text-muted font-weight-bold text-uppercase mb-3">
                    Template preview — auto-filled per venue on send
                </p>

                <div class="form-group">
                    <label class="form-control-label">Message Body</label>
                    <html-editor name="proposal-body-before" :value="formData.bodyBefore"
                        @input="formData.bodyBefore = $event"></html-editor>
                </div>

                <div v-if="hasDealInfo" class="deal-info-block mb-3">
                    <table class="table table-sm table-borderless deal-info-table">
                        <tbody>
                            <tr>
                                <td class="deal-info-label">Client Name</td>
                                <td><input v-model="dealInfo.client_name" class="form-control form-control-sm"
                                        placeholder="Client Name" /></td>
                            </tr>
                            <tr>
                                <td class="deal-info-label">Company Name</td>
                                <td><input v-model="dealInfo.company_name" class="form-control form-control-sm"
                                        placeholder="Company Name" /></td>
                            </tr>
                            <tr>
                                <td class="deal-info-label">Estimated Guests</td>
                                <td><input v-model="dealInfo.estimated_guests" class="form-control form-control-sm"
                                        placeholder="Estimated Guests" /></td>
                            </tr>
                            <tr>
                                <td class="deal-info-label">Event Date</td>
                                <td>
                                    <el-date-picker v-model="dealInfo.event_date" type="date" placeholder="Pick a date"
                                        format="dd MMM yyyy" value-format="yyyy-MM-dd" size="small"
                                        style="width: 100%;"></el-date-picker>
                                </td>
                            </tr>
                            <tr>
                                <td class="deal-info-label">Event Type</td>
                                <td>
                                    <select v-model="dealInfo.event_type" class="form-control form-control-sm">
                                        <option value="">— Select —</option>
                                        <option v-for="type in corporateEventTypes" :key="type.label"
                                            :value="type.label">{{ type.label }}</option>
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td class="deal-info-label">Seating Style</td>
                                <td>
                                    <select v-model="dealInfo.seating_style" class="form-control form-control-sm">
                                        <option value="">— Select —</option>
                                        <option v-for="s in seatingStyles" :key="s.label" :value="s.label">{{ s.label }}
                                        </option>
                                    </select>
                                </td>
                            </tr>

                            <tr>
                                <td class="deal-info-label">Guest Accommodation</td>
                                <td>
                                    <select v-model="dealInfo.guest_accommodation" class="form-control form-control-sm">
                                        <option :value="null">—</option>
                                        <option :value="true">Yes</option>
                                        <option :value="false">No</option>
                                    </select>
                                </td>
                            </tr>
                            <tr>
                                <td class="deal-info-label">Budget</td>
                                <td><input v-model="dealInfo.budget" class="form-control form-control-sm"
                                        placeholder="Budget" /></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div v-if="formData.bodyAfter !== null" class="form-group">
                    <html-editor name="proposal-body-after" :value="formData.bodyAfter"
                        @input="formData.bodyAfter = $event"></html-editor>
                </div>

                <div class="mt-2">
                    <label class="form-control-label text-muted small">Rep Signature (auto-filled)</label>
                    <div class="p-2 bg-white border rounded small text-muted" v-html="repSignature"></div>
                </div>
            </div>

            <base-button type="primary" native-type="submit" class="my-4"
                :disabled="!hasSelectedVenue || hasInvalidCc || hasMissingContactName || submitting"
                :loading="submitting">
                Send Proposal
            </base-button>
        </el-form>
    </div>
</template>

<script>
import { Form, FormItem, Checkbox, Alert, DatePicker } from "element-ui";
import { mapGetters } from "vuex";
import ProposalService from "@/services/proposal.service";
import HtmlEditor from "@/components/Inputs/HtmlEditor";
import moment from "moment-timezone";

export default {
    name: "new-proposal-form",
    props: {
        lead: {
            required: true,
        },
        shortlists: {
            type: Array,
            required: false,
        },
        leadEvent: {
            type: Object,
            default: () => ({}),
        },
        leadEventId: {
            required: true,
        },
        submitting: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        [FormItem.name]: FormItem,
        [Form.name]: Form,
        [Checkbox.name]: Checkbox,
        [HtmlEditor.name]: HtmlEditor,
        [Alert.name]: Alert,
        [DatePicker.name]: DatePicker,
    },
    data() {
        return {
            loading: false,
            fetchError: null,
            formData: {
                venues: {},
                bodyBefore: "",
                bodyAfter: null,
            },
            hasDealInfo: false,
            emailOverrides: {},
            ccEmails: {},
            ccInvalid: {},
            contactNameOverrides: {},
            contactNameInvalid: {},
            venueInfo: {},
            event: {},
            owner: {},
            userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            corporateEventTypes: [],
            seatingStyles: [],
            dealInfo: {
                client_name: '',
                company_name: '',
                estimated_guests: '',
                event_date: '',
                event_type: '',
                seating_style: '',
                guest_accommodation: null,
                budget: '',
            },
        };
    },
    async mounted() {
        if (this.shortlists && this.shortlists.length) {
            await this.fetchProposalInfo();
        }
    },
    watch: {
        shortlists(newVal) {
            if (newVal && newVal.length) {
                this.fetchProposalInfo();
            }
        },
    },
    computed: {
        ...mapGetters({
            currentUser: "auth/getUser",
        }),
        eventStartDisplay() {
            return this.formatInUserTimezone(this.leadEvent && this.leadEvent.event_start, "DD MMM YYYY");
        },
        hasSelectedVenue() {
            return Object.values(this.formData.venues).some((v) => v === true);
        },
        hasInvalidCc() {
            return Object.values(this.ccInvalid).some((v) => v === true);
        },
        hasMissingContactName() {
            if (! this.shortlists) return false;
            return this.shortlists
                .filter((v) => this.formData.venues[this.venueKey(v)] === true)
                .some((v) => !this.getEffectiveContactName(v));
        },
        repSignature() {
            let repName, repPhoto, repPhone;
            if (this.owner && this.owner.name) {
                repName = this.owner.name;
                repPhoto = this.owner.photo || "";
                repPhone = this.owner.phone || "";
            } else {
                return "";
            }

            const photoHtml = repPhoto
                ? `<td style="vertical-align:top; padding-right:16px;">
            <img src="${repPhoto}" alt="${repName}" style="width:52px; height:52px; border-radius:50%; object-fit:cover; display:block;">
           </td>`
                : "";

            const phoneHtml = repPhone
                ? `<p style="margin:4px 0 0; font-family:'Nunito',Arial,sans-serif; font-size:13px; color:#444444;"><strong>Ph:</strong> ${repPhone}</p>`
                : "";

            return `<tr>
                <td style="padding:24px 40px 36px;">
                <table cellpadding="0" cellspacing="0" border="0">
                    <tr>
                    ${photoHtml}
                    <td style="vertical-align:middle;">
                        <p style="margin:0; font-family:'Nunito',Arial,sans-serif; font-size:15px; font-weight:700; color:#1a1a2e;">${repName}</p>
                        <p style="margin:2px 0 0; font-family:'Nunito',Arial,sans-serif; font-size:13px; color:#666666;">Venue Specialist</p>
                        ${phoneHtml}
                    </td>
                    </tr>
                </table>
                </td>
            </tr>`;
        },
        eventDateDisplay() {
            const d = this.event.event_date;
            console.log({d});
            if (!d) return null;
            if (typeof d === 'object' && d.start) {
                return `${this.$options.filters.moment(d.start, 'DD MMM YYYY')} – ${this.$options.filters.moment(d.end, 'DD MMM YYYY')}`;
            }
            return this.$options.filters.moment(d, 'DD MMM YYYY');
        },
        guestsDisplay() {
            const g = this.event.no_of_guests;
            if (g === null || g === undefined) return null;
            if (typeof g === 'object') return `${g.min} – ${g.max}`;
            return g;
        },
        budgetDisplay() {
            const b = this.event.event_budget;
            if (b === null || b === undefined) return null;
            if (typeof b === 'object') return `$${b.min} – $${b.max}`;
            return `$${b}`;
        },
    },
    methods: {
        formatInUserTimezone(value, format) {
            if (! value) {
                return "";
            }

            if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
                return moment(value, "YYYY-MM-DD").format(format);
            }

            const parsedUtc = moment.utc(value);
            if (! parsedUtc.isValid()) {
                return "";
            }

            return parsedUtc.tz(this.userTimezone).format(format);
        },
        venueKey(venue) {
            return venue.is_custom_venue ? `custom_venue_${venue.id}` : `venue_${venue.id}`;
        },
        onVenueToggle(venue) {
            const key = this.venueKey(venue);

            if (! this.formData.venues[key]) {
                this.$delete(this.emailOverrides, key);
                this.$delete(this.ccEmails, key);
                this.$delete(this.ccInvalid, key);
                this.$delete(this.contactNameOverrides, key);
                this.$delete(this.contactNameInvalid, key);
            } else {
                this.ensureContactNamePrompt(venue);
            }
        },
        getDefaultContactName(venue) {
            const info = this.venueInfo[this.venueKey(venue)] || {};
            return info.inspection_contact_name || venue.inspection_contact_name || "";
        },
        getEffectiveContactName(venue) {
            const key = this.venueKey(venue);

            if (this.contactNameOverrides[key] !== undefined) {
                return (this.contactNameOverrides[key] || "").trim();
            }
            return this.getDefaultContactName(venue);
        },
        toggleContactNameOverride(venue) {
            const key = this.venueKey(venue);

            if (this.contactNameOverrides[key] !== undefined) {
                this.$delete(this.contactNameOverrides, key);
                this.$delete(this.contactNameInvalid, key);
            } else {
                this.$set(this.contactNameOverrides, key, this.getDefaultContactName(venue));
                this.validateContactName(key);
            }
        },
        validateContactName(venueId) {
            const val = this.contactNameOverrides[venueId];
            const empty = !val || !String(val).trim();
            this.$set(this.contactNameInvalid, venueId, empty);
        },
        ensureContactNamePrompt(venue) {
            const key = this.venueKey(venue);

            if (! this.getDefaultContactName(venue) && this.contactNameOverrides[key] === undefined) {
                this.$set(this.contactNameOverrides, key, "");
                this.$set(this.contactNameInvalid, key, true);
            }
        },
        toggleEmailOverride(venue) {
            const key = this.venueKey(venue);

            if (this.emailOverrides[key] !== undefined) {
                this.$delete(this.emailOverrides, key);
            } else {
                const fetchedEmail = this.venueInfo[key]?.inspection_contact_email;
                this.$set(this.emailOverrides, key, fetchedEmail || venue.inspection_contact_email || "");
            }
        },
        toggleCc(venue) {
            const key = this.venueKey(venue);

            if (this.ccEmails[key] !== undefined) {
                this.$delete(this.ccEmails, key);
                this.$delete(this.ccInvalid, key);
            } else {
                this.$set(this.ccEmails, key, "");
                this.$set(this.ccInvalid, key, false);
            }
        },
        parseCcList(raw) {
            if (!raw) return [];
            return raw
                .split(/[,;\s]+/)
                .map((e) => e.trim())
                .filter((e) => e.length > 0);
        },
        validateCc(venueId) {
            const list = this.parseCcList(this.ccEmails[venueId]);
            const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const invalid = list.some((e) => !emailRe.test(e));
            this.$set(this.ccInvalid, venueId, invalid);
        },
        buildPayload() {
            const selectedVenues = this.shortlists
                ? this.shortlists
                    .filter((v) => this.formData.venues[this.venueKey(v)] === true)
                    .map((v) => {
                        const key = this.venueKey(v);
                        const info = this.venueInfo[key] || {};
                        const defaultEmail = info.inspection_contact_email || v.inspection_contact_email || "";
                        return {
                            venue_id: v.id,
                            is_custom_venue: v.is_custom_venue,
                            venue_name: v.name,
                            contact_name: this.getEffectiveContactName(v),
                            email:
                                this.emailOverrides[key] !== undefined
                                    ? this.emailOverrides[key]
                                    : defaultEmail,
                            email_overridden: this.emailOverrides[key] !== undefined,
                            cc: this.parseCcList(this.ccEmails[key]),
                        };
                    })
                : [];

            const body = this.hasDealInfo
                ? (this.formData.bodyBefore || '') + this.buildDealInfoHtml() + (this.formData.bodyAfter || '')
                : (this.formData.bodyBefore || '');

            return {
                venues: selectedVenues,
                body,
                deal_info: {
                    client_name: this.dealInfo.client_name || null,
                    company_name: this.dealInfo.company_name || null,
                    event_date: this.dealInfo.event_date || null,
                    event_type: this.dealInfo.event_type || null,
                    estimated_guests: this.dealInfo.estimated_guests || null,
                    seating_style: this.dealInfo.seating_style || null,
                    guest_accommodation: this.dealInfo.guest_accommodation,
                    budget: this.dealInfo.budget || null,
                },
                rep_signature: this.repSignature,
            };
        },
        async fetchProposalInfo() {
            this.loading = true;
            this.fetchError = null;
            try {
                const venues = this.shortlists.map((v) => { return {id: v.id, is_custom_venue: v.is_custom_venue} });
                const data = await ProposalService.getProposalInfo(venues, this.leadEventId);

                if (data.venues) {
                    this.venueInfo = data.venues;
                    if (this.shortlists) {
                        this.shortlists.forEach((v) => {
                            if (this.formData.venues[this.venueKey(v)]) {
                                this.ensureContactNamePrompt(v);
                            };
                        });
                    }
                }
                if (data.event) {
                    this.event = data.event;
                    this.populateDealInfo();
                }
                if (data.corporate_event_types) {
                    this.corporateEventTypes = data.corporate_event_types;
                }
                if (data.seating_styles) {
                    this.seatingStyles = data.seating_styles;
                }
                if (data.owner) {
                    this.owner = data.owner;
                }
                if (data.template && data.template.body) {
                    this.resolveTemplate(data.template.body);
                }
            } catch (err) {
                this.fetchError = "Failed to load venue / template information. You can still fill in the form manually.";
            } finally {
                this.loading = false;
            }
        },
        populateDealInfo() {
            const clientName = this.event.client_name ||
                `${this.lead.first_name || ''} ${this.lead.last_name || ''}`.trim();
            const company = this.event.company_name || this.lead.company;
            const eventDateSource = this.event.event_date_start || this.event.event_date || '';

            this.dealInfo.client_name = clientName || '';
            this.dealInfo.company_name = company || '';
            this.dealInfo.estimated_guests = this.guestsDisplay || '';
            this.dealInfo.event_date = this.formatInUserTimezone(eventDateSource, 'YYYY-MM-DD');
            this.dealInfo.event_type = this.event.corporate_event_type || this.event.event_type || '';
            this.dealInfo.seating_style = this.event.seating_style || '';
            this.dealInfo.guest_accommodation = this.event.guest_accommodation ?? null;
            this.dealInfo.budget = this.event.event_budget || '';
        },
        buildDealInfoHtml() {
            const accom = this.dealInfo.guest_accommodation;

            const rows = [
                ['Client Name', this.dealInfo.client_name || null],
                ['Company Name', this.dealInfo.company_name || null],
                ['Estimated Guests', this.dealInfo.estimated_guests || null],
                ['Event Date', this.dealInfo.event_date ? this.$options.filters.moment(this.dealInfo.event_date, 'DD MMM YYYY') : null],
                ['Event Type', this.dealInfo.event_type || null],
                ['Seating Style', this.dealInfo.seating_style || null],
                ['Guest Accommodation', (accom !== null && accom !== undefined && accom !== '') ? (accom ? 'Yes' : 'No') : null],
                ['Budget', this.dealInfo.budget ? `$${this.dealInfo.budget}` : null],
            ].filter(([, v]) => v !== null);

            const rowsHtml = rows.map(([label, value]) => `
                <tr>
                <td style="width:160px;font-family:'Nunito',Arial,sans-serif;font-size:13px;font-weight:700;color:#666666;padding:4px 16px 4px 0;vertical-align:top;">${label}</td>
                <td style="font-family:'Nunito',Arial,sans-serif;font-size:13px;color:#333333;padding:4px 0;">${value}</td>
                </tr>`).join('');

                    return `<table width="100%" cellpadding="0" cellspacing="0" border="0">
                ${rowsHtml}
            </table>`;
        },

        resolveTemplate(raw) {
            const firstName = this.lead.first_name ||
                (this.event.client_name || '').split(' ')[0] || '';
            const lastName = this.lead.last_name ||
                (this.event.client_name || '').split(' ').slice(1).join(' ') || '';
            const company = this.event.company_name || this.lead.company || '';

            let result = raw;
            result = result.replace(/\[CLIENT_FIRST_NAME\]/g, firstName);
            result = result.replace(/\[CLIENT_LAST_NAME\]/g, lastName);

            // {#COMPANY_NAME}...{^COMPANY_NAME} conditional block
            result = result.replace(
                /\{#COMPANY_NAME\}([\s\S]*?)\{\^COMPANY_NAME\}/g,
                (_, inner) => company ? inner.replace(/\[COMPANY_NAME\]/g, company) : ''
            );

            // Split on [DEAL_INFO] (with or without wrapping <p> tags)
            const dealInfoPattern = /(<p[^>]*>\s*\[DEAL_INFO\]\s*<\/p>|\[DEAL_INFO\])/i;
            const parts = result.split(dealInfoPattern);

            if (parts.length >= 3) {
                // parts[0] = before, parts[1] = the matched token, parts[2] = after
                this.hasDealInfo = true;
                this.formData.bodyBefore = parts[0] || '';
                this.formData.bodyAfter = parts.slice(2).join('') || '';
            } else {
                this.hasDealInfo = false;
                this.formData.bodyBefore = result;
                this.formData.bodyAfter = null;
            }
        },

        submitRequest() {
            if (! this.hasSelectedVenue) {
                return;
            }
            if (this.hasInvalidCc) {
                return;
            }
            if (this.hasMissingContactName) {
                this.shortlists
                    .filter((v) => this.formData.venues[this.venueKey(v)] === true)
                    .forEach((v) => {
                        const key = this.venueKey(v);

                        if (! this.getEffectiveContactName(v)) {
                            if (this.contactNameOverrides[key] === undefined) {
                                this.$set(this.contactNameOverrides, key, "");
                            }
                            this.$set(this.contactNameInvalid, key, true);
                        }
                    });
                return;
            }
            this.$emit("submitRequest", this.buildPayload());
        },
    },
};
</script>

<style scoped>
.email-template-preview {
    border: 1px dashed #ccc;
}

.venue-row:last-child {
    border-bottom: none !important;
}

.deal-info-block {
    margin: 8px 0;
    overflow: hidden;
    border-radius: 4px;
    background: #f8f9fe;
    padding: 12px 16px;
}

.deal-info-table {
    margin-bottom: 0;
}

.deal-info-table td {
    padding: 4px 8px;
    vertical-align: middle;
}

.deal-info-label {
    width: 160px;
    font-weight: 700;
    font-size: 13px;
    color: #666;
    white-space: nowrap;
}
</style>
